import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  GetWorkspaceRolesResponse,
  MappedWorskpaceForSupportResponse,
} from '../auth';
import { RootState } from '../root/root-types';
import { superAdminApi } from './api';
import { LastUpdated } from '../config';

export const SUPER_ADMIN_FEATURE_KEY = 'supeAdmin';

export interface SuperAdminState {
  rolesList: GetWorkspaceRolesResponse;
  selectedSupportUser: MappedWorskpaceForSupportResponse | null;
  wabaUsersList: any[];
  wabaUsersTemplateList: any[];
  refreshWabaUsersList: boolean;
  versionList: any[];
  wabaScreenLoading: boolean;
  lastUpdatedSuper: LastUpdated[];
}

export const initialSuperAdminState: SuperAdminState = {
  rolesList: { roles: [] },
  selectedSupportUser: null,
  wabaUsersList: [],
  wabaUsersTemplateList: [],
  refreshWabaUsersList: false,
  versionList: [],
  wabaScreenLoading: false,
  lastUpdatedSuper: [{ lastUpdated: '', updatedDate: '' }],
};

export const superAdminSlice = createSlice({
  name: SUPER_ADMIN_FEATURE_KEY,
  initialState: initialSuperAdminState,
  reducers: {
    onGetWorkspaceRolesSuccess(
      state,
      action: PayloadAction<GetWorkspaceRolesResponse>
    ) {
      state.rolesList = action?.payload;
    },
    updateWorkspaceRoles(
      state,
      action: PayloadAction<GetWorkspaceRolesResponse>
    ) {
      state.rolesList = action?.payload;
    },
    updateSelectedSupportUser(
      state,
      action: PayloadAction<MappedWorskpaceForSupportResponse>
    ) {
      state.selectedSupportUser = action.payload;
    },
    updateWabaUsersList(state, action: PayloadAction<any[]>) {
      state.wabaUsersList = action.payload;
    },
    updateWabaUsersTemplateList(state, action: PayloadAction<any[]>) {
      state.wabaUsersTemplateList = action.payload;
    },
    updateRefreshWabaUsersList(state, action: PayloadAction<boolean>) {
      state.refreshWabaUsersList = action.payload;
    },
    updateVersionList(state, action: PayloadAction<any[]>) {
      state.versionList = action.payload;
    },
    updateWabaScreenLoading(state, action: PayloadAction<boolean>) {
      state.wabaScreenLoading = action.payload;
    },
    updateLastUpdatedSuper(
      state,
      action: PayloadAction<{ lastUpdated: string; updatedDate: string }>
    ) {
      const { lastUpdated, updatedDate } = action.payload;
      const existingEntryIndex = state.lastUpdatedSuper?.findIndex(
        (entry) => entry.updatedDate === updatedDate
      );
      if (existingEntryIndex && existingEntryIndex !== -1) {
        if (
          state.lastUpdatedSuper &&
          state.lastUpdatedSuper[existingEntryIndex]
        ) {
          state.lastUpdatedSuper[existingEntryIndex] = {
            ...state.lastUpdatedSuper[existingEntryIndex],
            lastUpdated,
          };
        }
      } else {
        state.lastUpdatedSuper?.push({ lastUpdated, updatedDate });
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      superAdminApi.endpoints.getWorkspaceRolesList.matchFulfilled,
      (state, action) => {
        superAdminSlice.caseReducers.onGetWorkspaceRolesSuccess(state, action);
      }
    );
  },
});

export const superAdminReducer = superAdminSlice.reducer;

export const {
  updateWorkspaceRoles,
  updateSelectedSupportUser,
  updateWabaUsersList,
  updateWabaUsersTemplateList,
  updateRefreshWabaUsersList,
  updateVersionList,
  updateWabaScreenLoading,
  updateLastUpdatedSuper,
} = superAdminSlice.actions;

export const getSuperAdminState = (rootState: RootState): SuperAdminState =>
  rootState[SUPER_ADMIN_FEATURE_KEY];

export const getRolesList = createSelector(
  getSuperAdminState,
  (state) => state?.rolesList
);

export const getSelectedSupportUser = createSelector(
  getSuperAdminState,
  (state) => state.selectedSupportUser
);

export const getUpdatedWabaUsersList = createSelector(
  getSuperAdminState,
  (state) => state.wabaUsersList
);

export const getWabaUsersTemplateList = createSelector(
  getSuperAdminState,
  (state) => state.wabaUsersTemplateList
);

export const getRefreshWabaUsersList = createSelector(
  getSuperAdminState,
  (state) => state.refreshWabaUsersList
);

export const getVersionList = createSelector(
  getSuperAdminState,
  (state) => state.versionList
);
export const getWabaScreenLoading = createSelector(
  getSuperAdminState,
  (state) => state.wabaScreenLoading
);
export const getLastUpdatedSuper = createSelector(
  getSuperAdminState,
  (state) => state.lastUpdatedSuper
);
